import {
  alpha,
  Avatar,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
  Typography
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { formatLeaseDay } from "utils/listings/lease";
import { addMonthsToDay } from "utils/listings/entryCongrats";
import type { ChangeEvent } from "react";
import type {
  EntryCongratsCandidate,
  EntryCongratsDraft,
  EntryCongratsDue,
  EntryCongratsPlan
} from "utils/listings/entryCongrats";

type EntryCongratsBarProps = {
  loading: boolean;
  /** The seller's name as the form holds it; empty when none is recorded. */
  sellerName: string;
  draft: EntryCongratsDraft;
  plan: EntryCongratsPlan;
  candidates: EntryCongratsCandidate[];
  entryDay: string | null;
  todayDay: string;
  onChange: (draft: EntryCongratsDraft) => void;
};

/**
 * "Remind a seller-side realtor to congratulate on the entry", in the sold dialog. It sits in
 * the form, after both sides of the deal and before פרטים נוספים (a bar pinned above the
 * buttons was tried first and rejected), so a realtor filling the deal in walks past it.
 */
export function EntryCongratsBar({
  loading,
  sellerName,
  draft,
  plan,
  candidates,
  entryDay,
  todayDay,
  onChange
}: EntryCongratsBarProps): JSX.Element {
  const { t: translate } = useTranslation();

  const assignee = candidates.find((candidate) => candidate.email === plan.assigneeEmail);
  const isMine = !assignee || assignee.isMe;

  const dueLabel = (due: EntryCongratsDue): string =>
    due === "entryDay"
      ? translate("listingsPage.entryCongrats.entryDay", { date: formatLeaseDay(entryDay ?? todayDay) })
      : translate(`listingsPage.entryCongrats.${due}`);

  const pickDue = (due: EntryCongratsDue) => (): void =>
    onChange({
      ...draft,
      due,
      // A custom day starts from the day the bar showed, so the field is never blank.
      customDay: due === "custom" ? draft.customDay ?? plan.day ?? addMonthsToDay(todayDay, 1) : draft.customDay
    });

  const handleCustomDay = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void =>
    onChange({ ...draft, customDay: value || null });

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        border: 1,
        borderRadius: 1,
        borderColor: (theme) => (draft.enabled ? alpha(theme.palette.primary.main, 0.24) : theme.palette.divider),
        bgcolor: (theme) => (draft.enabled ? alpha(theme.palette.primary.main, 0.08) : "transparent")
      }}
    >
      <FormControlLabel
        disabled={loading}
        control={
          <Checkbox
            checked={draft.enabled}
            onChange={({ target: { checked } }) => onChange({ ...draft, enabled: checked })}
          />
        }
        label={
          <Typography variant="subtitle2">
            {sellerName
              ? translate("listingsPage.entryCongrats.toggle", { name: sellerName })
              : translate("listingsPage.entryCongrats.toggleNoName")}
          </Typography>
        }
      />
      {draft.enabled && (
        <Stack gap={1} sx={{ pl: { sm: 4 }, pb: 0.5 }}>
          {candidates.length > 1 && (
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64 }}>
                {translate("calendarPage.officeTasks.assignee")}
              </Typography>
              {candidates.map((candidate) => {
                const isSelected = candidate.email === plan.assigneeEmail;

                return (
                  <Chip
                    key={candidate.email}
                    disabled={loading}
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    avatar={<Avatar src={candidate.avatarUrl}>{candidate.name.charAt(0)}</Avatar>}
                    label={candidate.isMe ? translate("calendarPage.officeTasks.me") : candidate.name}
                    onClick={() => onChange({ ...draft, assigneeEmail: candidate.email })}
                  />
                );
              })}
            </Stack>
          )}
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64 }}>
              {translate("listingsPage.entryCongrats.when")}
            </Typography>
            {plan.options.map((due) => (
              <Chip
                key={due}
                disabled={loading}
                color={plan.due === due ? "primary" : "default"}
                variant={plan.due === due ? "filled" : "outlined"}
                label={dueLabel(due)}
                onClick={pickDue(due)}
              />
            ))}
            {plan.due === "custom" && (
              <TextField
                required
                type="date"
                size="small"
                disabled={loading}
                value={draft.customDay ?? ""}
                inputProps={{ min: todayDay, "aria-label": translate("listingsPage.entryCongrats.dateLabel") }}
                onChange={handleCustomDay}
              />
            )}
          </Stack>
          {plan.day && (
            <Typography variant="caption" color="text.secondary">
              {isMine
                ? translate("listingsPage.entryCongrats.helperMine", { date: formatLeaseDay(plan.day) })
                : translate("listingsPage.entryCongrats.helperColleague", {
                    date: formatLeaseDay(plan.day),
                    name: assignee?.name
                  })}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
